import { useState } from 'react';

import { Box, Divider, Flex, Heading, Text } from '@joinrepublic/dls-ui-kit';

import { useScrollToHashTarget } from '../../hooks/use-scroll-to-hash-target';
import { useSortQueryParam } from '../../hooks/use-sort-query-param';
import {
  GROWTH_STAGE_VENTURES_ANCHOR_ID,
  HEADING,
  SUBHEADER,
} from './constants';
import { OfferingsGrid } from './offerings-grid/offerings-grid';
import { OfferingsGridSkeleton } from './offerings-grid/offerings-grid-skeleton/offerings-grid-skeleton';
import { SectorFilter } from './sector-filter/sector-filter';
import { SortByMenu } from './sort-by-menu/sort-by-menu';

/**
 * The open offerings section of the /investment-opportunities page — the growth
 * stage ventures grid.
 *
 * Unlike the neighbouring sections the design gives this one no "See all" link
 * and no tooltip — the sort and sector controls take that row instead.
 *
 * Sort lives in `?sort=` so a sorted view can be linked (see
 * `useSortQueryParam`), which makes `SortByMenu` controlled. The sector filter
 * is deliberately not in the URL: `SectorFilter` still owns its selection and
 * this component only mirrors it for the grid.
 *
 * The section carries the page's one anchor, so it can be linked directly —
 * `#growth-stage-ventures`, with or without `?sort=`.
 */
export const GrowthStageVentures = () => {
  const [sectorSlug, setSectorSlug] = useState<string | null>(null);
  const { sort, isRouterReady, setSort } = useSortQueryParam();

  useScrollToHashTarget(GROWTH_STAGE_VENTURES_ANCHOR_ID, isRouterReady);

  return (
    <Box
      as="section"
      id={GROWTH_STAGE_VENTURES_ANCHOR_ID}
      // Gives the section a name, which makes it a `region` landmark — it is a
      // link target, so it can be the first thing a reader lands on.
      aria-label={HEADING}
      // Keeps the heading off the viewport edge when arrived at via the anchor.
      scrollMarginTop="8"
    >
      {/* Not `SectionHeader`: this section has no "See all" link and no count,
          so only the heading itself is shared — but the type scale has to match
          its siblings, which is what the responsive size is for. */}
      <Heading
        as="h2"
        size={{ base: 'title-sm', sm: 'title-md' }}
        noDefaultSpacing
      >
        {HEADING}
      </Heading>

      <Text
        mt="1"
        size={{ base: 'body-sm-md', sm: 'body-md' }}
        variant="text-secondary"
      >
        {SUBHEADER}
      </Text>

      {/* One row at every width: below `md` the sector filter is a menu beside
          the sort one, not the scrolling chip row that needed its own line. */}
      <Flex
        mt="6"
        gap={{ base: '8', md: '5' }}
        align="center"
        justifyContent="start"
      >
        <SortByMenu value={sort} onChange={setSort} />

        <Divider
          orientation="vertical"
          height="7"
          borderColor="neutral.border-subtle"
          display={{ base: 'none', md: 'block' }}
        />

        {/* `minWidth="0"` lets the sector row overflow and scroll. Without it the
            flex item sizes to its content, so it never overflows and the scroll
            arrows never appear. On mobile it also keeps the menu trigger from
            growing, so its label truncates instead of pushing the controls apart. */}
        <Box flex={{ base: '0 1 auto', md: '1' }} minWidth="0">
          <SectorFilter onSelectedSlugChange={setSectorSlug} />
        </Box>
      </Flex>

      <Box mt={{ base: '5', md: '6' }}>
        {/* The page is statically optimized, so `router.query` is empty until the
            router is ready. Mounting the grid before then would fire a request
            for the default sort that a `?sort=` deep link immediately
            supersedes — one wasted round trip and a visible re-sort. The grid's
            own skeleton stands in, so the gate reads as ordinary loading. */}
        {isRouterReady ? (
          <OfferingsGrid sectorSlug={sectorSlug} sort={sort} />
        ) : (
          <OfferingsGridSkeleton />
        )}
      </Box>
    </Box>
  );
};
